import React, {useState} from 'react';

// Components
import {Button, Grid, TextField, makeStyles} from '@material-ui/core';

// Libraries
import {capitalize} from 'lodash';
import {useDispatch} from 'react-redux';
import {useToast} from '@dishtech-react/hooks';

// Variables and constants
import {
  AVAIL_ENDPOINT,
  AVAIL_ERRORS_ENDPOINT,
  availableAvailSearchOptions,
  availableErrorSearchOptions,
  availableSearchTypes,
  filterTypes,
} from '../../../../helpers/constants';

// Redux Actions
import {
  findAllResults,
  findAvailByPriceTypeAndCurrencyResults,
  findAvailBySimpleSearchResults,
  findErrorsByALIDOrEIDRResults,
  findErrorsBySystemAndSeverityResults,
} from '../../../../redux/actions/results-actions';
import RenderSelectComponent from './render-select-component';

const useStyles = makeStyles(theme => ({
  componentWrapper: {
    padding: `${theme.spacing(1)}px ${theme.spacing(5)}px`,
  },
  formWrapper: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  valueInput: {
    flexGrow: 1,
    flexShrink: 0,
  },
  searchTypeSelect: {
    flexGrow: 1,
  },
  filterOptionsFormControl: {
    display: 'flex',
    gap: theme.spacing(3),
    flexDirection: 'row',
  },
  filterWrapper: {
    width: '100%',
    display: 'flex',
    gap: theme.spacing(3),
  },
  formGrid: {
    padding: theme.spacing(1),
  },
  formSectionTitle: {
    margin: 0,
    marginBottom: theme.spacing(0.5),
  },
  priceFilterInput: {
    width: '18%',
  },
}));

const SearchForm = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {createToast} = useToast();
  const [selectedSearchOption, setSelectedSearchOption] = useState(availableErrorSearchOptions[0].value);
  const [filterOptions, setFilterOptions] = useState({
    system: '*',
    severity: '*',
  });

  const [selectedEndpoint, setSelectedEndpoint] = useState(availableSearchTypes[1].value);

  const handleFilterChange = event => {
    const {name, value} = event.target;
    setFilterOptions(prev => ({
      ...prev,
      [name]: value,
    }));
  };
  const simpleSearchOptions =
    selectedEndpoint === AVAIL_ERRORS_ENDPOINT ? availableErrorSearchOptions : availableAvailSearchOptions;

  const handleSearchTypeChange = event => setSelectedSearchOption(event.target.value);

  const handleTermSearchSubmit = evt => {
    evt.preventDefault();
    evt.stopPropagation();

    const formValues = new FormData(evt.target);
    const valueFromForm = formValues.get(selectedSearchOption);

    if (!valueFromForm) {
      return createToast('Value cannot be blank', {
        variant: 'error',
        persist: true,
      });
    }

    if (selectedEndpoint === AVAIL_ENDPOINT) {
      dispatch(
        findAvailBySimpleSearchResults({
          endpoint: selectedEndpoint,
          searchType: selectedSearchOption,
          valueToFind: valueFromForm,
          params: filterOptions,
        })
      );
    } else if (selectedEndpoint === AVAIL_ERRORS_ENDPOINT) {
      dispatch(
        findErrorsByALIDOrEIDRResults({
          endpoint: selectedEndpoint,
          searchType: selectedSearchOption,
          valueToFind: valueFromForm,
          params: filterOptions,
        })
      );
    }
  };

  const handleFilterErrorsBySystemAndSeverity = evt => {
    evt.preventDefault();
    evt.stopPropagation();

    const {severity, system} = filterOptions;

    const checkFilterValidity = value => value && value !== '*';

    if (!checkFilterValidity(severity) && !checkFilterValidity(system)) {
      return createToast('Need to select at least one filter', {
        variant: 'error',
        persist: true,
      });
    }

    dispatch(
      findErrorsBySystemAndSeverityResults({
        params: {
          severity,
          system,
          ...filterOptions,
        },
      })
    );
  };

  const handleFilterAvailByPriceSubmit = evt => {
    evt.preventDefault();
    evt.stopPropagation();

    const formValues = new FormData(evt.target);

    const price = formValues.get('price');
    const type = formValues.get('type');
    const currency = formValues.get('currency');

    if (!price || !type) {
      return createToast('Required fields must be filled', {
        variant: 'error',
        persist: true,
      });
    }

    dispatch(
      findAvailByPriceTypeAndCurrencyResults({
        params: {
          ...filterOptions,
          price,
          type,
          currency,
        },
      })
    );
  };

  const handleSearchUnfiltered = () => {
    dispatch(findAllResults({endpoint: selectedEndpoint, limit: filterOptions.limit}));
  };

  return (
    <div className={classes.componentWrapper}>
      <Grid container={true}>
        <Grid className={classes.formGrid} item={true} xs={6}>
          <div className={classes.filterWrapper} style={{marginBottom: '16px'}}>
            <p>
              <strong>Searching for</strong>
            </p>
            <RenderSelectComponent
              className={classes.searchTypeSelect}
              handleChange={evt => setSelectedEndpoint(evt.target.value)}
              key="Search Type"
              label="Search Type"
              name="Search Type"
              optionsArray={availableSearchTypes}
              startingValue={availableSearchTypes[0].value}
              value={selectedEndpoint}
            />
          </div>
          <div>
            <Button color="primary" variant="contained" onClick={handleSearchUnfiltered}>
              {' '}
              Find all {`${selectedEndpoint === AVAIL_ENDPOINT ? 'avails' : 'errors'}`}
            </Button>
          </div>
        </Grid>
        <Grid className={classes.formGrid} item={true} xs="6">
          <h5 className={classes.formSectionTitle}>Search by value</h5>
          <form className={classes.formWrapper} onSubmit={handleTermSearchSubmit}>
            <TextField className={classes.valueInput} id="value" label="Value" name={selectedSearchOption} />
            <RenderSelectComponent
              classes={classes.searchTypeSelect}
              handleChange={handleSearchTypeChange}
              label="Search by"
              name="search-type"
              optionsArray={simpleSearchOptions}
              startingValue={simpleSearchOptions[0].value}
              value={selectedSearchOption}
            />
            <Button color="primary" type="submit" variant="contained">
              Search By {selectedSearchOption}
            </Button>
          </form>
          {selectedEndpoint === AVAIL_ENDPOINT && (
            <>
              <h5 className={classes.formSectionTitle}>Filter avails by price</h5>
              <form className={classes.formWrapper} onSubmit={handleFilterAvailByPriceSubmit}>
                <TextField className={classes.priceFilterInput} id="price" label="Price" name="price" />
                <TextField className={classes.priceFilterInput} id="type" label="Type" name="type" />
                <TextField
                  className={classes.priceFilterInput}
                  id="currency"
                  label="Currency (optional)"
                  name="currency"
                  style={{flexGrow: 1}}
                />
                <Button color="primary" type="submit" variant="contained">
                  avail search
                </Button>
              </form>
            </>
          )}
          {selectedEndpoint === AVAIL_ERRORS_ENDPOINT && (
            <>
              <h5 className={classes.formSectionTitle}>Filter Avails</h5>
              <form className={classes.filterWrapper} onSubmit={handleFilterErrorsBySystemAndSeverity}>
                {filterTypes.map(filter => (
                  <RenderSelectComponent
                    classes={classes.searchTypeSelect}
                    handleChange={handleFilterChange}
                    key={filter.name}
                    label={capitalize(filter.name)}
                    name={filter.name}
                    optionsArray={filter.options}
                    startingValue={filter.options[0].value}
                    value={filterOptions[filter.name]}
                  />
                ))}
                <Button color="primary" type="submit" variant="contained">
                  filtered search
                </Button>
              </form>
            </>
          )}
        </Grid>
      </Grid>
    </div>
  );
};

export default SearchForm;
